import type { Settings, TenantResource, UpdateSettingsRequest, UpdateTenantRequest } from '@/types/settings';

/**
 * Estado del formulario de "Ajustes generales" y traducción a los dos `PATCH` que guarda.
 *
 * La pestaña General toca DOS endpoints distintos, así que el armado del payload no se hace
 * en el JSX: se aísla aquí, en funciones puras, para poder verificarlo sin navegador (el
 * smoke comprueba que solo viaja lo que cambió y que vaciar un campo manda `null`).
 *
 *  - `PATCH /api/tenants/:tenantId` → nombre del negocio y logo.
 *  - `PATCH /api/tenants/:tenantId/settings` → asistentes, descripción, políticas, tono,
 *    número del admin y reglas generales (el backend exige al menos un campo).
 */

/** Campos de texto del formulario (los `string | null` de los dos endpoints). */
export interface GeneralSettingsFormState {
  nombreEmpresa: string;
  logoUrl: string;
  nombreAsistenteVentas: string;
  nombreAsistentePostventa: string;
  descripcionEmpresa: string;
  politicas: string;
  personalidadTono: string;
  adminWhatsappNumber: string;
  /** Contexto del negocio (22-sep-2026): país, moneda y zona horaria del prompt. */
  pais: string;
  moneda: string;
  zonaHoraria: string;
  /** Instrucciones propias del negocio para cada asistente (22-sep-2026). */
  promptVentas: string;
  promptPostventa: string;
  /**
   * «Foto que no está en el catálogo» (23-sep-2026, solicitud especial de UN negocio): el interruptor y
   * el texto que recibe el cliente. La tarjeta solo se pinta cuando el backend dice que este negocio
   * tiene la función habilitada (`Settings.avisoFotoAdminDisponible`).
   */
  avisarFotoNoCatalogo: boolean;
  mensajeFotoNoCatalogo: string;
  /**
   * Guía de tallas del negocio (24-sep-2026): hasta DOS imágenes, en el orden en que se van a mandar
   * (la primera es la que el cliente ve primero). `[]` = sin guía.
   */
  guiaTallasFotos: string[];
  /** Reglas obligatorias del negocio (se pueden dejar filas vacías: se descartan al enviar). */
  reglasGenerales: string[];
}

/** Nombres de campo del formulario (para repartir los mensajes del backend con `Field`). */
export const GENERAL_FORM_FIELDS = [
  'nombreEmpresa',
  'logoUrl',
  'nombreAsistenteVentas',
  'nombreAsistentePostventa',
  'descripcionEmpresa',
  'politicas',
  'personalidadTono',
  'adminWhatsappNumber',
  'pais',
  'moneda',
  'zonaHoraria',
  'promptVentas',
  'promptPostventa',
  'avisarFotoNoCatalogo',
  'mensajeFotoNoCatalogo',
  'guiaTallasFotos',
  'reglasGenerales',
] as const;

/** Claves de `settings` que son texto libre (se comparan y se envían igual). */
/** Texto del formulario → valor de la API: vacío es `null` (el backend lo acepta para vaciar). */
function normalizeText(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Valor a enviar para un campo de texto: `undefined` si no cambió (no viaja), `null` si el
 * usuario lo vació, y el texto ya recortado si cambió.
 */
function changedText(next: string, previous: string): string | null | undefined {
  const normalized = normalizeText(next);
  if (normalized === normalizeText(previous)) return undefined;
  return normalized;
}

/** Reglas limpias: se descartan las filas vacías (el backend exige 1..500 por regla). */
export function normalizedRules(rules: string[]): string[] {
  return rules.map((rule) => rule.trim()).filter((rule) => rule.length > 0);
}

function sameRules(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((rule, index) => rule === b[index]);
}

/** Datos del servidor → estado del formulario (los `null` se pintan como cadenas vacías). */
export function generalSettingsFormStateFrom(
  tenant: TenantResource,
  settings: Settings,
): GeneralSettingsFormState {
  return {
    nombreEmpresa: tenant.nombreEmpresa,
    logoUrl: tenant.logoUrl ?? '',
    nombreAsistenteVentas: settings.nombreAsistenteVentas ?? '',
    nombreAsistentePostventa: settings.nombreAsistentePostventa ?? '',
    descripcionEmpresa: settings.descripcionEmpresa ?? '',
    politicas: settings.politicas ?? '',
    personalidadTono: settings.personalidadTono ?? '',
    adminWhatsappNumber: settings.adminWhatsappNumber ?? '',
    pais: settings.pais ?? '',
    moneda: settings.moneda ?? '',
    zonaHoraria: settings.zonaHoraria ?? '',
    promptVentas: settings.promptVentas ?? '',
    promptPostventa: settings.promptPostventa ?? '',
    avisarFotoNoCatalogo: settings.avisarFotoNoCatalogo ?? false,
    mensajeFotoNoCatalogo: settings.mensajeFotoNoCatalogo ?? '',
    guiaTallasFotos: settings.guiaTallasFotos ?? [],
    reglasGenerales: settings.reglasGenerales ?? [],
  };
}

export interface GeneralSettingsPayload {
  /** `null` = el negocio no cambió (no se llama al endpoint). */
  tenant: UpdateTenantRequest | null;
  /** `null` = los ajustes no cambiaron. */
  settings: UpdateSettingsRequest | null;
}

/**
 * Diff contra lo que había cuando se cargó el formulario: solo viaja lo que cambió.
 * Evita los dos males clásicos: un `PATCH` con `{}` (el backend responde 400 "Debes enviar al
 * menos un campo") y pisar con `null` un campo que el usuario no tocó.
 *
 * Los campos se escriben uno por uno (y no recorriendo un array de claves) para que
 * TypeScript compruebe el nombre exacto de cada propiedad del body contra el contrato.
 */
export function generalSettingsPayload(
  form: GeneralSettingsFormState,
  loaded: GeneralSettingsFormState,
): GeneralSettingsPayload {
  const nombreEmpresa = form.nombreEmpresa.trim();
  const nombreAsistenteVentas: string | null | undefined = changedText(
    form.nombreAsistenteVentas,
    loaded.nombreAsistenteVentas,
  );
  const nombreAsistentePostventa = changedText(
    form.nombreAsistentePostventa,
    loaded.nombreAsistentePostventa,
  );
  const descripcionEmpresa = changedText(form.descripcionEmpresa, loaded.descripcionEmpresa);
  const politicas = changedText(form.politicas, loaded.politicas);
  const personalidadTono = changedText(form.personalidadTono, loaded.personalidadTono);
  const adminWhatsappNumber = changedText(
    form.adminWhatsappNumber,
    loaded.adminWhatsappNumber,
  );
  const pais = changedText(form.pais, loaded.pais);
  const moneda = changedText(form.moneda, loaded.moneda);
  const zonaHoraria = changedText(form.zonaHoraria, loaded.zonaHoraria);
  const promptVentas = changedText(form.promptVentas, loaded.promptVentas);
  const promptPostventa = changedText(form.promptPostventa, loaded.promptPostventa);
  // El interruptor es un boolean: viaja solo si cambió (y no admite `null`).
  const avisarFotoNoCatalogo =
    form.avisarFotoNoCatalogo !== loaded.avisarFotoNoCatalogo
      ? form.avisarFotoNoCatalogo
      : undefined;
  const mensajeFotoNoCatalogo = changedText(
    form.mensajeFotoNoCatalogo,
    loaded.mensajeFotoNoCatalogo,
  );
  // La guía de tallas es una lista corta de URLs: misma limpieza que las reglas (se descartan los huecos)
  // y el ORDEN importa (la primera imagen es la que el cliente ve primero).
  const guiaFotos = normalizedRules(form.guiaTallasFotos);

  const tenant: UpdateTenantRequest = {
    ...(nombreEmpresa !== loaded.nombreEmpresa.trim() && { nombreEmpresa }),
    ...(form.logoUrl.trim() !== loaded.logoUrl.trim() && {
      logoUrl: form.logoUrl.trim().length > 0 ? form.logoUrl.trim() : null,
    }),
  };

  const rules = normalizedRules(form.reglasGenerales);
  const rulesChanged = !sameRules(rules, normalizedRules(loaded.reglasGenerales));
  const guiaChanged = !sameRules(guiaFotos, normalizedRules(loaded.guiaTallasFotos));

  const settings: UpdateSettingsRequest = {
    ...(nombreAsistenteVentas !== undefined && { nombreAsistenteVentas }),
    ...(nombreAsistentePostventa !== undefined && { nombreAsistentePostventa }),
    ...(descripcionEmpresa !== undefined && { descripcionEmpresa }),
    ...(politicas !== undefined && { politicas }),
    ...(personalidadTono !== undefined && { personalidadTono }),
    ...(adminWhatsappNumber !== undefined && { adminWhatsappNumber }),
    ...(pais !== undefined && { pais }),
    ...(moneda !== undefined && { moneda }),
    ...(zonaHoraria !== undefined && { zonaHoraria }),
    ...(promptVentas !== undefined && { promptVentas }),
    ...(promptPostventa !== undefined && { promptPostventa }),
    ...(avisarFotoNoCatalogo !== undefined && { avisarFotoNoCatalogo }),
    ...(mensajeFotoNoCatalogo !== undefined && { mensajeFotoNoCatalogo }),
    ...(guiaChanged && { guiaTallasFotos: guiaFotos }),
    ...(rulesChanged && { reglasGenerales: rules }),
  };

  return {
    tenant: Object.keys(tenant).length > 0 ? tenant : null,
    settings: Object.keys(settings).length > 0 ? settings : null,
  };
}
